import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { formatInviteCode } from '@h2h/shared';
import { AppShell, Content } from '../../components/ui/AppShell';
import { DesktopColumns } from '../../components/ui/DesktopColumns';
import { Avatar, MiniLeagueMark } from '../../components/ui/bits';
import { ArrowRight, Check, Copy, Share2 } from '../../components/ui/icons';
import { useHaptics } from '../../hooks/useHaptics';
import { useAuth } from '../../context/AuthContext';
import { useMiniLeague, useMiniLeagueStandings } from '../../hooks/useQueries';
import { ModalNav } from './parts';
import { MiniLeagueExplainer } from './MiniLeagueExplainer';
import { shareMiniLeagueInvite } from './miniLeagueHelpers';

export default function Share() {
  const navigate = useNavigate();
  const { impact } = useHaptics();
  const { id } = useParams<{ id: string }>();
  const { uid } = useAuth();
  const { data: mini, isLoading } = useMiniLeague(id);
  const { data: rows } = useMiniLeagueStandings(uid, mini);
  const [copied, setCopied] = useState(false);

  if (isLoading || !mini) {
    return (
      <AppShell>
        <Content>
          <div className="pt-20 text-center text-[14px] font-medium text-ink2">Loading…</div>
        </Content>
      </AppShell>
    );
  }

  const pretty = formatInviteCode(mini.inviteCode);
  const members = rows ?? [];

  const copy = async () => {
    impact('light');
    try {
      await navigator.clipboard.writeText(mini.inviteCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  };

  const share = async () => {
    impact('light');
    await shareMiniLeagueInvite(mini);
  };

  return (
    <AppShell>
      <Content>
        <ModalNav
          title="Invite friends"
          cancelLabel="Skip"
          onCancel={() => navigate(`/leagues/${mini.miniLeagueId}`, { replace: true })}
        />

        <DesktopColumns
          asideWidth="380px"
          stickyAside={false}
          main={
            <>
        <div className="relative flex flex-col items-center pt-4 text-center">
          <MiniLeagueMark icon={mini.icon} color={mini.color} size={88} />
          <h1 className="mt-4 text-[25px] font-extrabold leading-[1.05] tracking-title text-ink">
            {mini.name} is live!
          </h1>
          <p className="mx-auto mt-2 max-w-[280px] text-[14px] font-medium leading-[1.4] text-ink2">
            Send mates the code or link — they join instantly and their picks start counting.
          </p>
        </div>

        <div className="pt-5">
          <MiniLeagueExplainer variant="inline" />
        </div>

        <div className="pt-5">
          <div className="rounded-[20px] border-[1.5px] border-dashed border-ink4 bg-wash p-[18px] text-center">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink2">Join code</div>
            <div className="num mt-1.5 text-[38px] font-bold tracking-[0.08em] text-ink">{pretty}</div>
            <button
              type="button"
              onClick={copy}
              className="tap mt-3 inline-flex items-center gap-[7px] rounded-full bg-night px-4 py-[9px]"
            >
              {copied ? (
                <Check width={15} height={15} strokeWidth={2.6} className="text-accent" />
              ) : (
                <Copy width={15} height={15} strokeWidth={2} className="text-white" />
              )}
              <span className="text-[13px] font-extrabold tracking-snug text-white">
                {copied ? 'Copied' : 'Copy code'}
              </span>
            </button>
          </div>
        </div>

        <div className="pt-3.5">
          <button
            type="button"
            onClick={share}
            className="tap flex w-full items-center justify-center gap-[9px] rounded-[16px] bg-gradient-to-br from-accent to-accent-deep py-4 shadow-featured"
          >
            <Share2 width={18} height={18} strokeWidth={2.2} className="text-white" />
            <span className="text-[15px] font-extrabold tracking-snug text-white">Share invite link</span>
          </button>
        </div>
            </>
          }
          aside={
            <>
        <div className="flex items-baseline justify-between pb-2 pt-7 md:pt-0">
          <span className="text-[13px] font-extrabold tracking-snug text-ink">Members</span>
          <span className="text-[12px] font-semibold text-ink2">
            {mini.memberIds.length} {mini.memberIds.length === 1 ? 'so far' : 'in'}
          </span>
        </div>

        <div className="flex flex-col gap-2.5">
          {members.map((m) => {
            const isOwner = m.userId === mini.ownerId;
            return (
              <div
                key={m.userId}
                className="flex items-center gap-3 rounded-[18px] border border-black/6 bg-card p-[13px_15px]"
              >
                <Avatar name={m.displayName} tone={m.avatarTone} size={38} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[14px] font-extrabold tracking-snug text-ink">
                    {m.isYou ? 'You' : m.displayName}
                  </div>
                  <div className="text-[12px] font-medium text-ink2">{isOwner ? 'League admin' : 'Member'}</div>
                </div>
                {isOwner && (
                  <span className="rounded-[6px] bg-gradient-to-br from-gold-top to-gold-bottom px-2 py-[3px] text-[10px] font-extrabold tracking-wide text-gold-ink">
                    ADMIN
                  </span>
                )}
              </div>
            );
          })}

          {mini.memberIds.length === 1 && (
            <div className="flex items-center gap-2.5 px-1 pt-1">
              <div className="flex items-center">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className={`h-[30px] w-[30px] rounded-full border-[1.5px] border-dashed border-ink4 bg-fill ${i > 0 ? '-ml-2' : ''}`}
                  />
                ))}
              </div>
              <span className="text-[12px] font-medium text-ink3">Waiting for your mates to join…</span>
            </div>
          )}
        </div>
            </>
          }
        />

        <div className="pt-8">
          <button
            type="button"
            onClick={() => {
              impact('medium');
              navigate(`/leagues/${mini.miniLeagueId}`, { replace: true });
            }}
            className="tap flex w-full items-center justify-center gap-2 rounded-[17px] bg-night py-[17px]"
          >
            <span className="text-[16px] font-extrabold tracking-snug text-white">Go to league</span>
            <ArrowRight width={17} height={17} strokeWidth={2.6} className="text-white" />
          </button>
        </div>
      </Content>
    </AppShell>
  );
}
